export const WORLD_GEN_BASELINE_PROFILE = Object.freeze({
  // Global world seed. Changing this single value remaps terrain + structures.
  worldSeed: 48763,

  // Ecosystem region scale.
  ecosystemScale: 1.0,

  // Terrain relief and underground depth tuning.
  terrainVerticalScale: 1.0,
  undergroundDepthScale: 1.0,

  // Large-structure presentation scale.
  structureScale: 1.0,

  // Compensates landmark spawn chance when structure cells are enlarged.
  structureDensityCompensation: 1.0,

  // World bounds / waterline.
  worldMinY: -10,
  waterLevel: 6,

  // Structure placement grid size (larger values = broader ecosystem landmarks).
  settlementCellSize: 96,
  poiCellSize: 72,
  shipwreckCellSize: 112,
  skyFeatureCellSize: 88,

  // Streaming/perf guardrails.
  maxChunksPerFrame: 4,
  chunkBuildColumnsPerFrame: 28,
  surfaceColumnDepth: 6,
  fullColumnDepth: 18
});

function defineWorldGenPreset(overrides) {
  return Object.freeze({
    ...WORLD_GEN_BASELINE_PROFILE,
    ...(overrides || {})
  });
}

export const WORLD_GEN_PRESETS = Object.freeze({
  // Legacy behavior before world-scale expansion.
  legacy: WORLD_GEN_BASELINE_PROFILE,

  // Recommended: larger ecosystems + strong vertical scale for survival gameplay.
  survival: defineWorldGenPreset({
    ecosystemScale: 2.0,
    terrainVerticalScale: 1.72,
    undergroundDepthScale: 1.82,
    structureScale: 1.6,
    structureDensityCompensation: 1.0,
    worldMinY: -24,
    waterLevel: 8,
    settlementCellSize: 192,
    poiCellSize: 144,
    shipwreckCellSize: 224,
    skyFeatureCellSize: 176,
    maxChunksPerFrame: 3,
    chunkBuildColumnsPerFrame: 24,
    surfaceColumnDepth: 8,
    fullColumnDepth: 32
  }),

  // More landmarks and exploration beats, with slightly denser structure cadence.
  adventure: defineWorldGenPreset({
    ecosystemScale: 1.85,
    terrainVerticalScale: 1.55,
    undergroundDepthScale: 1.6,
    structureScale: 1.5,
    structureDensityCompensation: 1.18,
    worldMinY: -21,
    waterLevel: 8,
    settlementCellSize: 176,
    poiCellSize: 132,
    shipwreckCellSize: 208,
    skyFeatureCellSize: 160,
    maxChunksPerFrame: 3,
    chunkBuildColumnsPerFrame: 24,
    surfaceColumnDepth: 8,
    fullColumnDepth: 30
  }),

  // Maximum spectacle for megabuilds; slightly lower landmark density by default.
  builder: defineWorldGenPreset({
    ecosystemScale: 2.3,
    terrainVerticalScale: 1.82,
    undergroundDepthScale: 1.86,
    structureScale: 1.7,
    structureDensityCompensation: 0.9,
    worldMinY: -24,
    waterLevel: 8,
    settlementCellSize: 224,
    poiCellSize: 168,
    shipwreckCellSize: 256,
    skyFeatureCellSize: 208,
    maxChunksPerFrame: 3,
    chunkBuildColumnsPerFrame: 22,
    surfaceColumnDepth: 9,
    fullColumnDepth: 34
  })
});

// Switch here to change global world-gen behavior:
// 'survival' | 'adventure' | 'builder' | 'legacy'
export const WORLD_GEN_ACTIVE_PRESET = 'survival';

export const WORLD_GEN_PROFILE = Object.freeze(
  WORLD_GEN_PRESETS[WORLD_GEN_ACTIVE_PRESET] || WORLD_GEN_PRESETS.survival
);
